import { useState } from "react";
import { motion } from "framer-motion";
import { FileText } from "lucide-react";

import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import { cn } from "../../../../lib/utils";
import ClaimInfoDialog from "./claimInfoDialog";
import { useClaimMotion } from "./claimMotion";
import { describeClaimStatus, resolveClaimStage } from "./claimStatus";

/**
 * The identity of the claim: its case number, when it was filed, and where it
 * currently stands.
 *
 * One layout at every width now. There used to be two branches — a bordered
 * card for phone and tablet, a bare row for desktop — which is why the "Claim
 * Details" button existed only on desktop and a customer on a phone had no way
 * to see what they had submitted.
 *
 * The status pill is new. CRM already told us the claim was "Pending For Loss
 * Adjustor"; the page just never showed it, so the stepper was the only clue
 * and it only resolves to five coarse stages.
 */
const ClaimHeader = ({ claimCaseNumber, claimDate, details }) => {
  const translate = useLanguageTranslate();
  const motionPresets = useClaimMotion();
  const [infoOpen, setInfoOpen] = useState(false);
  const crmStatus = details?.Claim?.ClaimStatus;
  // The stage drives the pill's colour, through the same `.claim-item .{stage}`
  // classes the claims list uses. The detailed CRM status rides alongside it as
  // plain text — "Settlement" tells the customer where they are, "Pending For
  // Loss Adjustor" tells them what is actually happening.
  const stage = resolveClaimStage(crmStatus);
  const detail = describeClaimStatus(crmStatus);

  return (
    <motion.header
      {...motionPresets.rise}
      className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-[#DDE2E4] bg-[#F6F8F9] p-4 sm:p-5"
    >
      <div className="min-w-0">
        <p className="text-xs font-normal leading-4 text-[#6E7C87]">
          {translate("Case number", "رقم الحالة")}
        </p>
        <h1 className="mt-0.5 break-words text-xl font-semibold leading-7 tracking-[-0.2px] text-[#1A2024] sm:text-2xl sm:leading-8">
          {claimCaseNumber}
        </h1>

        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <span
            className={cn(
              "claim-item h-5 items-center rounded-[10px] px-2 py-0 text-center text-xs font-semibold uppercase leading-4 tracking-[0.12px] text-white",
              // The stage name IS the colour class. An unrecognised status has
              // no class, so it gets the neutral grey inline rather than
              // rendering white text on nothing.
              stage.key || "bg-[#84919a]"
            )}
          >
            {translate(stage.label_en, stage.label_ar)}
          </span>

          {detail && (
            <span className="text-xs font-medium leading-4 text-[#48535B]">
              {translate(detail.label_en, detail.label_ar)}
            </span>
          )}

          {claimDate && (
            <span className="text-xs font-normal leading-4 text-[#6E7C87]">
              · {translate("Filed", "تاريخ الفتح")} {claimDate}
            </span>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() => setInfoOpen(true)}
        className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-[#DDE2E4] bg-white px-4 py-2.5 text-sm font-semibold text-[#1A2024] transition-colors hover:bg-[#F0F3F5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A9E7] focus-visible:ring-offset-2"
      >
        <FileText className="size-4 text-[#5B6871]" aria-hidden="true" />
        {translate("Claim details", "تفاصيل المطالبة")}
      </button>

      <ClaimInfoDialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        details={details}
      />
    </motion.header>
  );
};

export default ClaimHeader;
